import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { MonitoringListItem, MonitoringListResponse, MonitoringSummary, SecureCandidate } from '@tww/shared';
import { DEFAULT_MONITORING_SETTINGS, IMPROVEMENT_LABELS, fmtDate, windowLabel } from '@tww/shared';
import { api, qs } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../ui/Modal';
import { ErrorState, Panel, Skeleton } from '../../ui/basics';
import { useToast } from '../../ui/Toast';
import { MapsLink, plantingLabel } from '../areas/common';
import { DuePill, YEAR_OPTIONS, conditionLabel, monitoringTitle, useUsersPick } from './common';
import { Dropdown } from '../../ui/Dropdown';

const STATUS_OPTIONS: { value: string; label: string; count: (c: MonitoringListResponse['counters']) => number }[] = [
  { value: '', label: 'Alle', count: (c) => c.all },
  { value: 'due', label: 'Fällig', count: (c) => c.due + c.overdue },
  { value: 'overdue', label: 'Überfällig', count: (c) => c.overdue },
  { value: 'in_progress', label: 'In Bearbeitung', count: (c) => c.in_progress },
  { value: 'done', label: 'Erledigt', count: (c) => c.done },
  { value: 'inquiry', label: 'Nachfragen', count: (c) => c.inquiry },
  { value: 'secure', label: 'Vorschlag gesichert', count: (c) => c.secure_candidates },
];

/** Monitoring-Jahresliste (Plan 05 Abschnitt 5.1, Prototyp Screen 07): Jahr wählbar, Filter als Auswahlfelder, Gruppen je Bundesland mit Hotels, Zuständigkeit auch für mehrere Flächen */
export function MonitoringListPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useAuth();
  const [sp, setSp] = useSearchParams();
  const thisYear = new Date().getFullYear();
  const year = Number(sp.get('year') ?? thisYear);
  const status = sp.get('status') ?? '';
  const state = sp.get('state') ?? '';
  const assigned = sp.get('assigned') ?? '';
  const q = sp.get('q') ?? '';
  const set = (patch: Record<string, string | number | null>) => {
    const n = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '' || (k === 'year' && Number(v) === thisYear)) n.delete(k);
      else n.set(k, String(v));
    }
    setSp(n, { replace: true });
  };
  const write = can('monitoring', 'write');
  const users = useUsersPick();
  const list = useQuery({
    queryKey: ['monitorings', year, status === 'secure' ? '' : status, state, assigned, q],
    queryFn: () => api.get<MonitoringListResponse>(`/monitorings${qs({ year, status: status === 'secure' ? '' : status, state, assigned, q })}`),
    placeholderData: (p) => p,
  });
  const sum = useQuery({ queryKey: ['monitoring-summary', year], queryFn: () => api.get<MonitoringSummary>(`/monitorings/summary${qs({ year })}`) });
  const cands = useQuery({ queryKey: ['secure-candidates'], queryFn: () => api.get<{ items: SecureCandidate[] }>('/monitorings/secure-candidates').then((r) => r.items) });
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkUser, setBulkUser] = useState('');
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['monitorings'] });
    qc.invalidateQueries({ queryKey: ['monitoring-summary'] });
    qc.invalidateQueries({ queryKey: ['secure-candidates'] });
    qc.invalidateQueries({ queryKey: ['areas'] });
    qc.invalidateQueries({ queryKey: ['today'] });
  };
  const assign = useMutation({
    mutationFn: (p: { ids: number[]; user: number | null }) => api.post<{ changed: number }>('/monitorings/assign', { ids: p.ids, assigned_user_id: p.user }),
    onSuccess: (r) => {
      toast(r.changed === 1 ? 'Zuständigkeit gesetzt' : `${r.changed} Monitorings zugewiesen`);
      setSelected([]);
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const items = list.data?.items ?? [];
  const grouped = useMemo(() => {
    const groups = list.data?.groups ?? [];
    return groups.map((g) => ({ g, rows: items.filter((i) => (i.state_code ?? null) === g.state_code) })).filter((x) => x.rows.length);
  }, [list.data, items]);
  const openIds = items.filter((i) => i.status === 'planned' || i.status === 'in_progress').map((i) => i.id);
  const counters = list.data?.counters;
  const facets = list.data?.facets;
  const s = sum.data;
  const settings = list.data?.settings ?? DEFAULT_MONITORING_SETTINGS;
  const win = windowLabel(settings.window);
  return (
    <>
      <div className="head">
        <div>
          <div className="title">
            <h1>Monitoring</h1>
            <Dropdown className="inline" aria-label="Jahr" value={year} onChange={(v) => set({ year: Number(v) })} clearable={false} options={YEAR_OPTIONS(thisYear).map((y) => ({ value: String(y), label: String(y) }))} />
          </div>
          <div className="sub">{s ? `${s.in_monitoring} Flächen im Monitoring · ${s.secured} gesichert · Zeitraum ${win}` : ''}</div>
        </div>
      </div>
      <div className="filterbar">
        <div className="field grow">
          <label htmlFor="m-q">Suche</label>
          <input id="m-q" value={q} onChange={(e) => set({ q: e.target.value })} placeholder="Fläche oder Ort" />
        </div>
        <div className="field">
          <label htmlFor="m-status">Stand</label>
          <Dropdown id="m-status" value={status} onChange={(v) => set({ status: v })} clearable={false} options={STATUS_OPTIONS.map((o) => ({ value: String(o.value), label: `${o.label}${counters ? ` (${o.count(counters)})` : ''}` }))} />
        </div>
        <div className="field">
          <label htmlFor="m-state">Bundesland</label>
          <Dropdown id="m-state" value={state} onChange={(v) => set({ state: v })} placeholder="Alle" options={(facets?.state ?? []).map((f) => ({ value: String(f.value), label: `${list.data?.groups.find((g) => g.state_code === f.value)?.state_label ?? f.value} (${f.count})` }))} />
        </div>
        <div className="field">
          <label htmlFor="m-assigned">Zuständigkeit</label>
          <Dropdown id="m-assigned" value={assigned} onChange={(v) => set({ assigned: v })} placeholder="Alle" options={[...(me ? [{ value: 'me', label: 'Für mich' }] : []), ...(facets?.assigned ?? []).map((f) => ({ value: String(f.value), label: `${f.label} (${f.count})` }))]} />
        </div>
        <div className="spacer" />
        {(status || state || assigned || q) && (
          <div className="opts">
            <button type="button" className="btn sm" onClick={() => set({ status: null, state: null, assigned: null, q: null })}>
              Zurücksetzen
            </button>
          </div>
        )}
      </div>

      {status === 'secure' ? (
        <SecurePanel items={cands.data ?? []} loading={cands.isLoading} write={write} onDone={invalidate} years={settings.secureAfterYears} />
      ) : (
        <>
          {write && selected.length > 0 && (
            <div className="bulkbar" role="region" aria-label="Auswahl">
              <strong>{selected.length} ausgewählt</strong>
              <Dropdown aria-label="Zuweisen an" value={bulkUser} onChange={(v) => setBulkUser(v)} placeholder="Zuweisen an …" options={[{ value: 'none', label: 'niemanden (Zuständigkeit entfernen)' }, ...(users.data ?? []).map((u) => ({ value: String(u.id), label: `${u.initials ? `${u.initials} · ` : ''}${u.display_name}` }))]} />
              <button type="button" className="btn primary sm" disabled={!bulkUser || assign.isPending} onClick={() => assign.mutate({ ids: selected, user: bulkUser === 'none' ? null : Number(bulkUser) })}>
                Zuweisen
              </button>
              <button type="button" className="btn sm" onClick={() => setSelected([])}>
                Auswahl aufheben
              </button>
            </div>
          )}
          <div className="panel">
            {list.isLoading ? (
              <Skeleton rows={8} />
            ) : list.isError ? (
              <ErrorState error={list.error} retry={() => list.refetch()} />
            ) : items.length === 0 ? (
              <div className="empty">Keine Monitorings in dieser Auswahl für {year}</div>
            ) : (
              <div className="tw">
                <table className="mon-table">
                  <thead>
                    <tr>
                      {write && (
                        <th style={{ width: 36 }}>
                          <input type="checkbox" aria-label="Alle offenen auswählen" checked={openIds.length > 0 && openIds.every((id) => selected.includes(id))} onChange={(e) => setSelected(e.target.checked ? openIds : [])} />
                        </th>
                      )}
                      <th>Fällig</th>
                      <th>Fläche</th>
                      <th>Pflanzung</th>
                      <th>Partner</th>
                      <th>Monitoring</th>
                      <th>Letzter Stand</th>
                      <th>Förster</th>
                      <th>Zuständig</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map(({ g, rows }) => (
                      <Fragment key={g.state_code ?? 'none'}>
                        <tr className="group">
                          <td colSpan={write ? 10 : 9}>
                            {g.state_label} · {rows.length} {rows.length === 1 ? 'Fläche' : 'Flächen'}
                            {g.hotels.length > 0 && (
                              <span className="mut" style={{ fontWeight: 400 }}>
                                {' '}
                                · Unterkunft:{' '}
                                {g.hotels.map((h, i) => (
                                  <span key={h.id}>
                                    {i > 0 && ', '}
                                    <Link className="link" to={`/kontakte/${h.id}`}>
                                      {h.display_name}
                                    </Link>
                                  </span>
                                ))}
                              </span>
                            )}
                          </td>
                        </tr>
                        {rows.map((m) => (
                          <Row key={m.id} m={m} write={write} users={users.data ?? []} selected={selected.includes(m.id)} onSelect={(v) => setSelected((s) => (v ? [...s, m.id] : s.filter((x) => x !== m.id)))} onAssign={(u) => assign.mutate({ ids: [m.id], user: u })} onOpen={() => nav(`/monitoring/${m.id}`)} />
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          {(cands.data?.length ?? 0) > 0 && <SecurePanel items={cands.data!} loading={false} write={write} onDone={invalidate} years={settings.secureAfterYears} />}
        </>
      )}
    </>
  );
}

function Row({ m, write, users, selected, onSelect, onAssign, onOpen }: { m: MonitoringListItem; write: boolean; users: { id: number; display_name: string; initials: string | null }[]; selected: boolean; onSelect: (v: boolean) => void; onAssign: (u: number | null) => void; onOpen: () => void }) {
  const open = m.status === 'planned' || m.status === 'in_progress';
  const phone = m.forester?.mobile ?? m.forester?.phone;
  return (
    <tr className={`row ${m.due.kind === 'overdue' ? 'late' : ''}`} onClick={onOpen}>
      {write && (
        <td>
          {open && <input type="checkbox" aria-label={`${m.area_name} auswählen`} checked={selected} onChange={(e) => onSelect(e.target.checked)} />}
        </td>
      )}
      <td>
        <DuePill due={m.due} status={m.status} />
        {m.status === 'done' && <span className="sm">{fmtDate(m.done_at)}</span>}
      </td>
      <td>
        <Link className="link" to={`/flaechen/${m.area_id}`}>
          {m.area_name}
        </Link>
        <span className="sm mon-place">
          {m.city ?? m.region}
          <MapsLink lat={m.lat} lng={m.lng} label="In Google Maps öffnen" compact />
        </span>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{plantingLabel(m)}</td>
      <td>{m.partner ? <Link className="link" to={`/kontakte/${m.partner.id}`}>{m.partner.display_name}</Link> : '–'}</td>
      <td>
        {monitoringTitle(m)}
      </td>
      <td>
        {m.last ? (
          <>
            {conditionLabel(m.last.condition)}
            <span className="sm">
              {m.last.year}
              {m.last.improvement_needed && m.last.improvement_needed !== 'nein' ? ` · Nachbesserung ${IMPROVEMENT_LABELS[m.last.improvement_needed]}` : ''}
            </span>
          </>
        ) : (
          <span className="mut">noch kein Befund</span>
        )}
      </td>
      <td>
        {m.forester ? (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <span>
              <Link className="link" to={`/kontakte/${m.forester.id}`}>
                {m.forester.display_name}
              </Link>
              {phone && (
                <span className="sm" style={{ whiteSpace: 'nowrap' }}>
                  <a href={`tel:${phone.replace(/\s/g, '')}`} onClick={(e) => e.stopPropagation()}>{phone}</a>
                </span>
              )}
            </span>
          </span>
        ) : (
          '–'
        )}
      </td>
      <td>
        {write && open ? (
          <Dropdown aria-label={`Zuständig für ${m.area_name}`} title={m.assigned?.display_name ?? 'niemand zuständig'} value={m.assigned?.id ?? ''} onChange={(v) => onAssign(v ? Number(v) : null)} size="sm" className="inline" placeholder="–" options={users.map((u) => ({ value: String(u.id), label: u.display_name }))} />
        ) : (
          <span title={m.assigned?.display_name ?? undefined}>{m.assigned?.initials ?? m.assigned?.display_name ?? m.done_by ?? '–'}</span>
        )}
      </td>
      <td className="num">
        {open ? (
          <Link className={`btn sm ${m.status === 'in_progress' ? '' : 'primary'}`} to={`/monitoring/${m.id}`}>
            {m.status === 'in_progress' ? 'Fortsetzen' : m.kind === 'inquiry' ? 'Nachfrage' : 'Erfassen'}
          </Link>
        ) : (
          <Link className="btn sm" to={`/monitoring/${m.id}`}>
            Ansehen
          </Link>
        )}
      </td>
    </tr>
  );
}

/** Panel „Vorschlag gesichert“ (6.4): Flächen älter als die eingestellte Frist, noch im Monitoring */
function SecurePanel({ items, loading, write, onDone, years }: { items: SecureCandidate[]; loading: boolean; write: boolean; onDone: () => void; years: number }) {
  const toast = useToast();
  const [confirm, setConfirm] = useState<SecureCandidate | null>(null);
  const m = useMutation({
    mutationFn: (id: number) => api.post<{ applied: string[] }>(`/areas/${id}/secure`, {}),
    onSuccess: (r) => {
      toast(r.applied.join(' · '));
      setConfirm(null);
      onDone();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Panel title={<>Vorschlag gesichert {items.length > 0 && <span className="mut" style={{ fontWeight: 400 }}>· {items.length}</span>}</>}>
      {loading ? (
        <Skeleton rows={3} />
      ) : items.length === 0 ? (
        <div className="empty">Keine Fläche ist älter als {years} Jahre und noch im Monitoring.</div>
      ) : (
        <>
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Fläche</th>
                  <th>Pflanzung</th>
                  <th>Partner</th>
                  <th>Letzter Befund</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <Link className="link" to={`/flaechen/${c.id}`}>
                        {c.name}
                      </Link>
                      <span className="sm">{[c.state_label, c.region].filter(Boolean).join(', ')}</span>
                    </td>
                    <td>
                      {c.planting_year ?? '–'}
                      <span className="sm">{c.age_years} Jahre</span>
                    </td>
                    <td>{c.partner ?? '–'}</td>
                    <td>{c.last ? `${c.last.condition_label} (${c.last.year})` : <span className="mut">kein Befund</span>}</td>
                    <td className="num">
                      {write && (
                        <button type="button" className="btn sm" onClick={() => setConfirm(c)} title="Beendet das Monitoring, offene Termine entfallen">
                          Als gesichert markieren
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {confirm && <ConfirmDialog title="Kultur als gesichert markieren" text={<>„{confirm.name}“ wird auf „Gesichert“ gesetzt. Offene Monitoring-Termine und Nachfragen entfallen mit dem Grund „Kultur gesichert“.</>} confirmLabel="Als gesichert markieren" onConfirm={() => m.mutate(confirm.id)} onClose={() => setConfirm(null)} busy={m.isPending} />}
    </Panel>
  );
}
